import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import styles from '../../src/admin/modules/layoutBuilder/components/Layout.scss';
import useQuery from '../../modules/react/hooks/useQuery';
import useMutation from '../../modules/react/hooks/useMutation';
import withReactRouter from '../hocs/withReactRouter';
import Builder from '../components/Builder';

const GET_PAGE_LAYOUT_DATA = gql`
  query PageLayoutBuilderData($localeCode: String!, $typeId: ID!, $versionId: ID!) {
    pageLayout(localeCode: $localeCode, typeId: $typeId) {
      id
      version(versionId: $versionId) {
        id
        ...BuilderVersion
      }
    }
  }
  ${Builder.fragments.version}
`;

const ADD_PAGE_LAYOUT_VERSION = gql`
  mutation PageLayoutVersionAdd($pageLayoutId: ID!, $version: PageVersionInput!) {
    pageLayoutAddVersion(id: $pageLayoutId, version: $version) {
      pageLayout {
        id
        latestVersion {
          id
        }
      }
    }
  }
`;

const propTypes = {
  localeCode: PropTypes.string.isRequired,
  typeId: PropTypes.string.isRequired,
  versionId: PropTypes.string.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

const defaultProps = {};

const PageLayoutBuilder = ({
  localeCode,
  typeId,
  versionId,
  router: {
    push,
  },
}) => {
  const { data: queryData } = useQuery(
    GET_PAGE_LAYOUT_DATA,
    /*
     * Skipping the apollo cache for this query because not all page layout
     * widgets and sections have a unique ID have.
     * While section and widget may have unique content, the ID never changes
     * => the incorrect version of a page layout is returned from this query.
     */
    { variables: { localeCode, typeId, versionId }, fetchPolicy: 'network-only' },
  );
  const [pageLayoutAddVersion, { data }] = useMutation(ADD_PAGE_LAYOUT_VERSION);

  useEffect(() => {
    if (data) {
      const { pageLayout } = data.pageLayoutAddVersion;
      push(`/admin/locales/${localeCode}/types/${typeId}/page-layout/versions/${pageLayout.latestVersion.id}/edit`);
    }
  }, [data]);

  const onAddPageLayoutVersion = async (sections) => {
    const sectionsToSave = sections.map((section) => {
      const { type, data: sectionData, payload, widgets } = section;
      const sectionWidgets = widgets.map((widget) => {
        if (!widget) {
          return null;
        }

        const { type: widgetType, payload: widgetPayload, componentId } = widget;
        // do not return widget.id, let the database generate a new one
        return {
          type: widgetType,
          payload: widgetPayload,
          componentId,
        };
      });

      return {
        // do not return section.id, let the database return a new one
        type,
        data: sectionData,
        payload,
        widgets: sectionWidgets,
      };
    });

    const newVersion = {
      sections: sectionsToSave,
    };

    pageLayoutAddVersion(
      {
        variables: {
          pageLayoutId: queryData.pageLayout.id, version: newVersion,
        },
      },
    );
  };

  if (!queryData) {
    return null;
  }

  return (
    <div className={styles.layout}>
      <Builder
        pageLayout={queryData.pageLayout}
        version={queryData.pageLayout.version}
        onAddVersion={onAddPageLayoutVersion}
      />
    </div>
  );
};

PageLayoutBuilder.propTypes = propTypes;
PageLayoutBuilder.defaultProps = defaultProps;

export default withReactRouter(PageLayoutBuilder);
